import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { Small } from '../../typography';
import { colors, fontSizes, fontWeights, lineHeights, spacing } from '../../../defaultTheme';
import query from '../../../const/mediaQueries';
import { FA_EXCLAMATION_CIRCLE } from '../../../const/iconProps';
import { Button } from '../../atoms';
import SimpleModal from '../SimpleModal';
function findHighlightedDateByThemeName(themeName) {
    return (highlightDate) => {
        if (highlightDate instanceof Date) {
            return false;
        }
        return highlightDate.themeName === themeName;
    };
}
/**
 * lots of library style overrides.
 * If you need to customize this further you may do so with
 * StyledComponent(Datepicker) and targetting these classes
 */
export const StyledCalendarWrapper = styled.div `
  width: 100%;

  @media ${query.greaterThanMedium} {
    width: inherit;
  }

  .react-datepicker-wrapper {
    width: 100%;

    @media ${query.greaterThanMedium} {
      width: 100%;
    }
  }

  .react-datepicker {
    border: none;
    border-radius: ${spacing.medium}px;
    max-height: 100vh;
    overflow: auto;
    transform: translate3d(0, 0, 0);
    max-width: 366px;
    ${({ monthsShown }) => {
    return (monthsShown > 1 &&
        `
          display: flex;
          flex-flow: wrap;
          max-width: 732px;
        `);
}}
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker-popper[data-placement^='bottom'] {
    padding-top: 0;
  }

  .react-datepicker__header {
    background: ${colors.white};
    border: none;
    padding: ${spacing.small}px ${spacing.medium}px;
  }

  .react-datepicker__month-container {
    position: relative;
    float: none;
    width: 366px;
  }

  .react-datepicker__month-container
    + .react-datepicker__month-container::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50px;
    height: calc(100% - 50px);
    border-left: 1px solid ${({ theme }) => theme.datepicker.disabled};
  }

  @media ${query.lessThanMedium} {
    .react-datepicker {
      display: flex;
      flex-direction: column;
      max-width: 100%;
      margin-inline: auto;
      box-shadow: none;
      font-size: 1rem;
      margin-top: ${spacing.base}px;
    }

    .react-datepicker__month {
      margin: 0;
    }
  }

  .react-datepicker__day {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: ${spacing.xsmall}px;
    &:hover {
      background: ${colors.lightGrey};
    }
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    width: 40px;
    height: 40px;
    line-height: 2rem;
    border-radius: 4px;
  }

  .react-datepicker__week {
    display: flex;
    justify-content: space-around;
  }

  .react-datepicker__current-month,
  .react-datepicker__day-name,
  .react-datepicker__week {
    font-size: 14px;
    color: ${colors.black};
    font-family: 'Open Sans';
  }

  .react-datepicker__current-month {
    font-size: ${fontSizes.root}px;
  }

  .react-datepicker__current-month--hasMonthYearDropdown {
    display: none;
  }

  .react-datepicker__month-year-select {
    border: none;
    color: ${colors.black};
    font-family: 'Open Sans';
    font-size: ${fontSizes.root}px;
    font-weight: bold;
    line-height: 24px;
  }

  .react-datepicker__month {
    margin: 0 ${spacing.base}px;
  }

  .react-datepicker__day-names {
    font-weight: bold;
    display: flex;
    flex-direction: row;
    justify-content: space-around;
  }

  .react-datepicker__navigation-icon::before {
    border-color: ${({ theme }) => theme.datepicker.navIcon};
    border-width: 2px 2px 0 0;
    height: 6px;
    width: 6px;
    top: 8px;
  }

  .react-datepicker__navigation--next {
    right: 5px;
  }

  .react-datepicker__navigation--previous {
    left: 5px;
  }

  .react-datepicker__day--outside-month:empty {
    background: none;
  }

  .react-datepicker__day--today {
    font-weight: ${fontWeights.bold};
  }

  .react-datepicker__day--highlighted {
    font-weight: ${fontWeights.bold};

    ${({ theme }) => {
    var _a, _b;
    const legendTheme = (_b = (_a = theme.datepicker) === null || _a === void 0 ? void 0 : _a.legend) === null || _b === void 0 ? void 0 : _b.dueDate;
    const { fill, border, text } = legendTheme;
    return `
          color: ${text};
          background-color: ${fill};
          box-shadow: inset 0 0 0 2px ${border};
        `;
}}
  }

  .react-datepicker__day--selected {
    color: ${({ theme }) => theme.datepicker.selectedDateText};
    background-color: ${({ theme }) => theme.datepicker.legend.selectedDate.fill};
  }

  ${({ highlightDateThemeArr, theme }) => highlightDateThemeArr === null || highlightDateThemeArr === void 0 ? void 0 : highlightDateThemeArr.map((date, i) => {
    var _a;
    if (date instanceof Date) {
        return '';
    }
    const { themeName } = date;
    const legendTheme = ((_a = theme.datepicker) === null || _a === void 0 ? void 0 : _a.legend) && theme.datepicker.legend[themeName];
    if (themeName && legendTheme) {
        const { fill, border, text } = legendTheme;
        return `.react-datepicker__day--highlighted-${i}:not(.react-datepicker__day--outside-month:empty) {
                border-radius: 0.3rem;
                outline: 2px solid ${border};
                outline-offset: -2px;
                color: ${text};
                background-color: ${fill};
              }
            `;
    }
    return '';
}).join('\n')}

  .react-datepicker__day--today:not(.react-datepicker__day--selected) {
    font-weight: ${fontWeights.bold};
    color: ${({ theme }) => theme.datepicker.legend.currentDate};
  }

  .react-datepicker__day--selected:not(
      .react-datepicker__day--outside-month:empty
    ) {
    ${({ theme }) => {
    var _a, _b;
    const legendTheme = (_b = (_a = theme.datepicker) === null || _a === void 0 ? void 0 : _a.legend) === null || _b === void 0 ? void 0 : _b.selectedDate;
    const { fill, text } = legendTheme;
    return `
          color: ${text};
          font-weight: ${fontWeights.bold};
          background-color: ${fill};
          font-weight: 400;
        `;
}}
  }

  /* Handle highlighted dates in the past (disabled) */
  ${({ highlightDateThemeArr, theme }) => {
    var _a, _b, _c, _d, _e, _f;
    if (!highlightDateThemeArr) {
        return '';
    }
    const dueDateIndex = highlightDateThemeArr.findIndex(findHighlightedDateByThemeName('dueDate'));
    const pastDueDateIndex = highlightDateThemeArr.findIndex(findHighlightedDateByThemeName('pastDueDate'));
    const lateFeeDateIndex = highlightDateThemeArr.findIndex(findHighlightedDateByThemeName('lateFeeDate'));
    const dueDateTheme = ((_b = (_a = theme === null || theme === void 0 ? void 0 : theme.datepicker) === null || _a === void 0 ? void 0 : _a.legend) === null || _b === void 0 ? void 0 : _b.dueDate) || {};
    const pastDueDateTheme = ((_d = (_c = theme === null || theme === void 0 ? void 0 : theme.datepicker) === null || _c === void 0 ? void 0 : _c.legend) === null || _d === void 0 ? void 0 : _d.pastDueDate) || {};
    const lateFeeDateTheme = ((_f = (_e = theme === null || theme === void 0 ? void 0 : theme.datepicker) === null || _e === void 0 ? void 0 : _e.legend) === null || _f === void 0 ? void 0 : _f.lateFeeDate) || {};
    return `
        .react-datepicker__day--highlighted-${dueDateIndex}.react-datepicker__day--disabled:not(.react-datepicker__day--outside-month:empty) {
          color: ${colors.mediumGrey};
          background-color: ${theme.datepicker.disabled};
          box-shadow: inset 0 0 0 2px ${dueDateTheme.border};
        }

        .react-datepicker__day--highlighted-${pastDueDateIndex}.react-datepicker__day--disabled:not(.react-datepicker__day--outside-month:empty) {
          color: ${colors.mediumGrey};
          background-color: ${theme.datepicker.disabled};
          box-shadow: inset 0 0 0 2px ${pastDueDateTheme.border};
        }

        .react-datepicker__day--highlighted-${lateFeeDateIndex}.react-datepicker__day--disabled:not(.react-datepicker__day--outside-month:empty) {
          color: ${lateFeeDateTheme.text};
          background-color: ${lateFeeDateTheme.fill};
          box-shadow: inset 0 0 0 2px ${lateFeeDateTheme.border};
        }
      `;
}}

  .react-datepicker__day--disabled {
    /* '--disabled' is last so it overrides other styles if a date is disabled */
    background-color: ${({ theme }) => theme.datepicker.disabled};
    color: ${colors.black};
    font-weight: ${fontWeights.regular};
    box-sizing: content-box;
  }

  /*
     * React-datepicker started adding a hardcoded width starting from 4.9.0 and above
     * https://github.com/Hacker0x01/react-datepicker/blob/v4.9.0/src/stylesheets/datepicker.scss#L685
     */
  .react-datepicker__children-container {
    width: 100%;
    margin: 0;
    padding: 0;
  }

  /* Fix: selected date tail */
  .react-datepicker__day--keyboard-selected {
    border-radius: unset;
    background-color: inherit;
    color: unset;
  }

  .react-datepicker__day--selected.react-datepicker__day--outside-month {
    background-color: inherit;
    color: unset;
    box-shadow: unset;
    font-weight: normal;
  }
  /* End Fix*/
`;
export const CalendarIcon = styled(FontAwesomeIcon) `
  height: 20px;
`;
export const ErrorIcon = () => {
    return React.createElement(FontAwesomeIcon, { icon: FA_EXCLAMATION_CIRCLE, color: colors.error });
};
// datepicker footer styles start
export const FooterWrapper = styled.div `
  padding: ${spacing.base}px ${spacing.base}px 0 ${spacing.base}px;
  clear: both;
  font-size: ${fontSizes.small}px;
  line-height: ${lineHeights.largest};
  color: ${colors.black};
  font-family: 'Open Sans';
`;
export const StyledKey = styled.div `
  display: flex;
  flex-wrap: wrap;

  @media ${query.lessThanSmall} {
    flex-direction: column;
  }
`;
export const LegendDate = styled.div `
  display: flex;
  align-items: center;
  margin-bottom: ${spacing.small}px;
  margin-right: ${spacing.large}px;
  font-weight: ${fontWeights.semiBold};
  font-size: 14px;

  &:nth-child(even) {
    margin-right: 0;
  }

  ::before {
    margin-right: ${spacing.small}px;
    content: '';
    display: inline-block;
    height: 16px;
    min-height: 16px;
    width: 16px;
    min-width: 16px;
    border-radius: 3.6px;
    border-width: 2px;
    border-style: solid;

    ${({ theme, themeName }) => {
    var _a;
    const { fill, border } = (((_a = theme.datepicker) === null || _a === void 0 ? void 0 : _a.legend) && theme.datepicker.legend[themeName]) || {};
    return `
        background-color: ${fill};
        border-color: ${border};
      `;
}};
  }
  }
`;
export const StyledDisclaimer = styled(Small) `
  margin-top: ${spacing.small}px;
  text-align: justify;
  padding-bottom: 14px;
`;
export const DatePickerDesktopWrapper = styled(SimpleModal) `
  // unsetting the Modal Wrapper default styles
  left: unset;
  transform: unset;
  transition-property: unset;
  transition-duration: unset;
  transition-timing-function: unset;

  position: absolute;
  top: 20%;
  right: 7%;
  z-index: 1;
  background-color: ${colors.white};
  box-shadow: 0 25px 50px -12px ${({ theme }) => theme.datepicker.shadow};
  margin: 20px 0;

  .simple-modal-styled-footer {
    margin: 0 0 ${spacing.small}px 0;
    padding: 0;
  }

  @media ${query.greaterThanSmall} {
    width: auto;
    min-width: 200px;
  }
`;
export const CalenderIconContainer = styled.div `
  display: flex;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
  padding: 3px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: ${colors.lightGrey};
  }
`;
export const SelectButton = styled(Button) `
  @media ${query.lessThanMedium} {
    margin-bottom: ${spacing.base}px;
  }
  &:hover {
    background: ${colors.lightGrey};
  }
`;
// datepicker footer styles end
